<script setup lang="ts">
import ArchetypePage from '~/components/archetypes/ArchetypePage.vue';

useSeoMeta({
  title: 'Practical, Governed AI for GMP Quality Operations — QikSolve',
  description:
    'A practical approach to using AI in GMP quality operations with clear ownership, evidence, human review, evaluation, and controlled change.',
});

const page = {
  hero: {
    eyebrow: 'Practical, governed AI for GMP quality operations',
    title: 'Use AI in a way that supports good quality and earns trust',
    summary:
      'GMP teams can use AI to support quality work, but good outcomes depend on clear ownership, understandable evidence, human review, evaluation, and controlled change.',
    cta: { label: 'Discuss your GMP AI pathway', to: '/contact' },
  },
  intro: {
    title: 'Governance is part of good quality practice',
    content:
      'AI governance is an operating requirement, not only a policy or legal question. The aim is to make AI-assisted work understandable, reviewable, repeatable, and capable of improvement within the quality system your team already uses.',
    items: [
      {
        title: 'Clear ownership',
        description: 'Name who owns the process, the decision, the review, and the evidence when AI contributes to quality work.',
      },
      {
        title: 'Understandable evidence',
        description: 'Keep enough context to understand what AI contributed, what was reviewed, and what decision or action followed.',
      },
      {
        title: 'Controlled learning',
        description: 'Use evaluation, monitoring, review findings, and change control to improve the workflow without losing trust.',
      },
    ],
  },
  sections: [
    {
      title: 'The control domains that make trust credible',
      items: [
        {
          title: 'Boundary definition',
          description: 'Define the intended use, process impact, approved data and tools, and where AI output can influence a GMP record, decision, approval, release, or evidence set.',
        },
        {
          title: 'Traceability',
          description: 'Keep the relevant input context, output, reviewer action, decision, and version information understandable and retrievable.',
        },
        {
          title: 'Validation and evaluation',
          description: 'Define evaluation criteria and acceptance thresholds before release, then review effectiveness as the use case operates and changes. Where the applicable computerised-system context calls for it, consider the relevant Annex 11 expectations alongside GMP requirements.',
        },
        {
          title: 'Observability and escalation',
          description: 'Make quality signals, uncertainty, exceptions, missed reviews, and escalation ownership visible to the people who can act.',
        },
        {
          title: 'Change control',
          description: 'Assess changes to models, prompts, retrieval, rules, tools, and workflows for their potential effect on the controlled state.',
        },
      ],
    },
    {
      title: 'A planning model for AI output boundaries',
      content:
        'The level of governance should follow the effect of the output on the process. This planning model helps teams ask the right questions; it is not a universal regulatory or validation classification.',
      items: [
        {
          title: 'Informational support',
          description: 'Internal drafting, ideation, or non-controlled assistance with no direct decision authority.',
        },
        {
          title: 'Process guidance',
          description: 'Suggested steps or non-final summaries used to prepare work, with traceability and a named reviewer.',
        },
        {
          title: 'Decision-influencing output',
          description: 'Risk proposals, deviation support, or compliance-gap recommendations requiring accountable human decision ownership and stronger evidence.',
        },
        {
          title: 'Record-integrated output',
          description: 'Output incorporated into a controlled record, approval, release, or evidence set, requiring a complete attributable evidence chain and change-control linkage.',
        },
      ],
    },
    {
      title: 'Use a practical language for AI roles',
      content:
        'Predictive AI predicts data, generative AI produces content, and agentic AI coordinates actions. This is a planning language rather than a universal regulatory classification; the control response still depends on intended use and process impact.',
      items: [
        {
          title: 'Predictive AI',
          description: 'Assess performance against suitable data and acceptance criteria when the system produces a classification, measurement, probability, or prediction.',
        },
        {
          title: 'Generative AI',
          description: 'Evaluate the quality, grounding, completeness, and suitability of content, with an accountable reviewer defining acceptance and disposition.',
        },
        {
          title: 'Agentic AI',
          description: 'Test the workflow as well as generated content: approved sources, permissions, sequencing, escalation, human approval, and traceability.',
        },
      ],
    },
    {
      title: 'Oversight is a workflow, not a signature',
      content:
        'People should be able to understand how an AI-assisted quality decision was reviewed. The right pattern depends on process impact, reviewer role, acceptance criteria, disposition, escalation, and closure evidence.',
      items: [
        {
          title: 'Structured review gate',
          description: 'Use defined criteria and explicit accept, reject, rework, or escalate outcomes for process guidance and other reviewable outputs.',
        },
        {
          title: 'Dual-role review',
          description: 'Use technical and quality or compliance perspectives when an output may affect quality judgement or compliance direction.',
        },
        {
          title: 'Escalation by threshold',
          description: 'Define signals, owners, and response expectations before exceptions or uncertainty occur.',
        },
        {
          title: 'Controlled exception review',
          description: 'When the normal path cannot be followed, record the reason, apply compensating controls, and complete a time-bound retrospective review.',
        },
      ],
    },
  ],
  callout: {
    title: 'Start with the quality system you already use',
    content:
      'QikSolve helps teams connect AI use to intended use, quality objectives, process ownership, review, evidence, evaluation, and controlled change. Where deeper platform and technical capability is needed, explore the QxAIOS solution.',
    tone: 'boundary' as const,
  },
  relatedLinks: {
    title: 'Related practice areas',
    items: [
      {
        label: 'Regulatory compliance',
        description: 'How regulated organisations translate expectations into workable quality practice.',
        to: '/product/regulatory-compliance',
      },
      {
        label: 'SharePoint governance',
        description: 'An operating model for controlled information, permissions, and process structure in Microsoft 365.',
        to: '/product/sharepoint-governance',
      },
      {
        label: 'Training and enablement',
        description: 'Build the competency and review habits that make governance usable in real operations.',
        to: '/product/training',
      },
      {
        label: 'Choosing AI use cases for GMP quality work',
        description: 'Decide which quality and operational problems are suitable for AI assistance.',
        to: '/product/choosing-ai-use-cases-for-gmp-quality-work',
      },
      {
        label: 'Defining the GMP boundary for AI outputs',
        description: 'Understand when an AI output becomes part of controlled quality work.',
        to: '/product/defining-gmp-boundary-for-ai-outputs',
      },
      {
        label: 'Human oversight patterns for GMP AI workflows',
        description: 'Design review, disposition, escalation, and closure as a workflow.',
        to: '/product/human-oversight-for-gmp-ai-workflows',
      },
      {
        label: 'Evidence and traceability for GMP AI',
        description: 'Make AI use, review, and resulting decisions understandable.',
        to: '/product/evidence-and-traceability-for-gmp-ai',
      },
      {
        label: 'Evaluating GMP AI before and after release',
        description: 'Define acceptance criteria, monitoring, and effectiveness review.',
        to: '/product/evaluating-gmp-ai-before-and-after-release',
      },
      {
        label: 'Controlled change for GMP AI workflows',
        description: 'Assess model, prompt, retrieval, tool, and workflow changes under control.',
        to: '/product/controlled-change-for-gmp-ai-workflows',
      },
      {
        label: 'Scaling GMP AI through existing quality systems',
        description: 'Extend quality-system discipline rather than creating a parallel governance stack.',
        to: '/product/scaling-gmp-ai-through-existing-quality-systems',
      },
      {
        label: 'From Annex 22 to Agentic AI',
        description: 'Use a practical language for matching AI testing and oversight to process impact.',
        to: '/blog/from-annex-22-to-agentic-ai-practical-language-for-regulated-ai',
      },
      {
        label: 'AI governance in a PIC/S GMP manufacturer',
        description: 'Read the ECS Botanics case study on a risk-based, auditable governance model.',
        to: '/blog/ai-governance-framework-ecs-botanics-case-study',
      },
      {
        label: 'Explore QxAIOS',
        description: 'Explore the QxAIOS solution for platform and product-depth information.',
        to: 'https://qx.qiksolve.com',
      },
    ],
  },
  cta: {
    title: 'Ready to shape a trusted GMP AI pathway?',
    content:
      'Bring the quality use case, operating context, and evidence questions your team is working through. We can help identify a sensible next step.',
    label: 'Discuss your GMP AI pathway',
    to: '/contact',
  },
};
</script>

<template>
  <ArchetypePage v-bind="page" />
</template>
